/**
 * The rules for a new table's or field's grants, as the server checks them
 * (`backend/app/permissions/service/new_resources.py`):
 *
 * - Someone who isn't an administrator sees only their own groups and gives
 *   every permission to at least one of them; every administrator group then
 *   gets full access on its own.
 * - An administrator sees every group and gives every permission to at least
 *   one group; administrator groups get only what they're given. Their own
 *   administrator groups start with every permission, which they can take off.
 *
 * With only one group to choose from, it gets every permission and there's
 * nothing to choose.
 */

interface GrantGroup {
  user_group_id: string
  is_master_admin?: boolean
  members: readonly { user_id: string }[]
}

/** Shown to someone who isn't an administrator wherever they choose a new table's or field's grants. */
export const ADMINISTRATORS_GET_FULL_ACCESS = 'Administrator groups also get full access.'

/** The groups the user is in, from a group list (members are trimmed to them for non-administrators). */
export function getOwnGroupIds(
  groups: readonly { user_group_id: string; members: readonly { user_id: string }[] }[],
  userId: string | null
): ReadonlySet<string> {
  if (userId === null) return new Set()
  return new Set(
    groups
      .filter((group) => group.members.some((member) => member.user_id === userId))
      .map((group) => group.user_group_id)
  )
}

/** The one group there is to choose from, which gets every permission locked on. */
export function getOnlyGroupId(groups: readonly { user_group_id: string }[]): string | null {
  return groups.length === 1 ? groups[0].user_group_id : null
}

/**
 * The groups that start with every permission: the only group, else an
 * administrator's own administrator groups. Someone else with several groups
 * chooses.
 */
export function getDefaultGrantGroupIds(
  groups: readonly GrantGroup[],
  { userId, isAdministrator }: { userId: string | null; isAdministrator: boolean }
): string[] {
  const onlyGroupId = getOnlyGroupId(groups)
  if (onlyGroupId) return [onlyGroupId]
  if (!isAdministrator) return []
  const own = getOwnGroupIds(groups, userId)
  return groups
    .filter((group) => group.is_master_admin && own.has(group.user_group_id))
    .map((group) => group.user_group_id)
}

/**
 * The permissions the creator wouldn't get through their own groups. None for
 * an administrator, whose rule is `getUngrantedActions`.
 */
export function getCreatorMissingActions<T extends { id: string }>(
  actions: readonly T[],
  {
    ownGroupIds,
    isAdministrator,
    isChecked,
  }: {
    ownGroupIds: ReadonlySet<string>
    isAdministrator: boolean
    isChecked: (groupId: string, actionId: string) => boolean
  }
): T[] {
  if (isAdministrator) return []
  return actions.filter(
    (action) => ![...ownGroupIds].some((groupId) => isChecked(groupId, action.id))
  )
}

/** The permissions no listed group gets. */
export function getUngrantedActions<T extends { id: string }>(
  actions: readonly T[],
  {
    groupIds,
    isChecked,
  }: {
    groupIds: readonly string[]
    isChecked: (groupId: string, actionId: string) => boolean
  }
): T[] {
  return actions.filter((action) => !groupIds.some((groupId) => isChecked(groupId, action.id)))
}

/** What still blocks a new table's or field's grants, and how to say it. */
export function getNewGrantGaps<T extends { id: string }>(
  actions: readonly T[],
  {
    groupIds,
    ownGroupIds,
    isAdministrator,
    isChecked,
  }: {
    groupIds: readonly string[]
    ownGroupIds: ReadonlySet<string>
    isAdministrator: boolean
    isChecked: (groupId: string, actionId: string) => boolean
  }
): { missing: T[]; message: string } {
  return isAdministrator
    ? {
        missing: getUngrantedActions(actions, { groupIds, isChecked }),
        message: 'Give each permission to at least one group',
      }
    : {
        missing: getCreatorMissingActions(actions, { ownGroupIds, isAdministrator, isChecked }),
        message: 'Give yourself every permission through one of your groups',
      }
}
